import {
  Box,
  Card,
  ChevronUpIcon,
  Flex,
  JaneGoldIcon,
  TagFilledIcon,
  Typography,
} from '@iheartjane/reefer';
import type { QualifyingStatusEnum } from '@iheartjane/types';
import { clsx } from 'clsx';

import { ProgressBar } from '../specialDetailStatusBundleProgress/progressBar';

import styles from '../specialDetailStatus.module.css';

/** Props for the mobile sticky summary bar at the bottom of the screen */
export interface SpecialDetailStatusStickyProps {
  /** Secondary text for the callout */
  calloutSubtext?: string;
  /** Primary callout message */
  calloutText?: string;
  /** Whether this is a Jane Gold special */
  isGold?: boolean;
  /** Callback fired when the sticky bar is tapped to expand details */
  onClick: () => void;
  /** Whether to display the progress bar in grams instead of currency */
  progressInGrams?: boolean;
  /** Total value/weight of qualified products for the progress bar */
  qualifiedProductsTotal?: number;
  /** Whether to show a spend/weight progress bar */
  qualifyBySpendAmount?: boolean;
  /** Current qualifying status of the special */
  qualifyingStatus?: QualifyingStatusEnum;
  /** Target amount needed to qualify */
  qualifyingTargetAmount?: number;
  /** Secondary text displayed after a bullet separator */
  subtext?: string;
  /** Primary text (e.g., brand name or special title) */
  text: string;
}

export const SpecialDetailStatusSticky = ({
  onClick,
  qualifyingStatus,
  calloutSubtext,
  calloutText,
  isGold,
  progressInGrams,
  qualifiedProductsTotal,
  qualifyBySpendAmount,
  qualifyingTargetAmount,
  subtext,
  text,
}: SpecialDetailStatusStickyProps) => {
  const isApplied = qualifyingStatus === 'applied';
  const isQualified = qualifyingStatus === 'qualified';
  const notQualified = qualifyingStatus === 'not_qualified';
  const isUnlocked = isApplied || isQualified;

  return (
    <Card
      border="transparent-black-ten"
      width="100%"
      ariaLabel="view special details"
      onClick={onClick}
    >
      <Card.Content>
        <Flex gap={8} alignItems="center">
          <Flex>
            {isGold ? (
              <JaneGoldIcon
                className={clsx({
                  [styles['opacity__not__unlocked']]: notQualified,
                })}
              />
            ) : (
              <TagFilledIcon color="info" />
            )}
          </Flex>
          <Box overflow="hidden">
            <Typography
              truncateAt="100%"
              overflowWrap="break-word"
              variant="body-semibold"
            >
              {text}
              {subtext && (
                <Typography mx={4} as="span" color="grays-mid">
                  &#8226;
                </Typography>
              )}
              {subtext && (
                <Typography as="span" color="grays-mid">
                  {subtext}
                </Typography>
              )}
            </Typography>

            {calloutText && (
              <Typography truncateAt="100%">
                <Typography as="span" color="grays-mid">
                  {calloutText}
                </Typography>

                {calloutSubtext && (
                  <Typography color="grays-mid" as="span" ml={4}>
                    {calloutSubtext}
                  </Typography>
                )}
              </Typography>
            )}
          </Box>

          <Flex ml="auto">
            <ChevronUpIcon />
          </Flex>
        </Flex>
        {qualifyBySpendAmount && !isUnlocked && (
          <ProgressBar
            progressInGrams={progressInGrams}
            qualifiedProductsTotal={qualifiedProductsTotal || 0}
            qualifyingTargetAmount={qualifyingTargetAmount || 0}
            unlocked={isUnlocked}
            pt={8}
          />
        )}
      </Card.Content>
    </Card>
  );
};
